/** layoutVariant value stored on dietplan docs for differentiated multi-day plans */
export const LAYOUT_VARIANT_PER_DAY = "perDayRange";

/** max inclusive days between from and to (local calendar) */
export const PER_DAY_RANGE_MAX_DAYS = 7;

/** soft cap per schedule per day before warning */
export const PER_DAY_SOFT_MAX_RECIPES_PER_SLOT = 50;

/** Meal slot field names (client table + PDF). */
export const MEAL_SLOT_KEYS = [
  "earlyMorning",
  "breakfast",
  "midMorning",
  "brunch",
  "midMeal",
  "lunch",
  "postLunch",
  "evening",
  "beforeDinner",
  "dinner",
  "postDinner",
  "lateNight",
];

/**
 * Validates from/to strings (YYYY-MM-DD). Returns { ok, dayCount?, message? }.
 */
export function validatePerDayDateRange(fromStr, toStr) {
  if (!fromStr || !toStr) {
    return { ok: false, message: "Start and end dates are required." };
  }
  const start = parsePerDayYmdToLocalDate(fromStr);
  const end = parsePerDayYmdToLocalDate(toStr);
  if (Number.isNaN(+start) || Number.isNaN(+end)) {
    return { ok: false, message: "Invalid date format." };
  }
  if (end < start) {
    return { ok: false, message: "End date must be on or after start date." };
  }
  const dayCount =
    Math.round((end - start) / 86400000) + 1;
  if (dayCount > PER_DAY_RANGE_MAX_DAYS) {
    return {
      ok: false,
      message: `Range cannot exceed ${PER_DAY_RANGE_MAX_DAYS} days.`,
    };
  }
  return { ok: true, dayCount };
}

/**
 * Shifts a local calendar date (YYYY-MM-DD) by deltaDays; invalid input yields "".
 * @param {string} ymd
 * @param {number} deltaDays
 * @returns {string}
 */
function addCalendarDaysYmd(ymd, deltaDays) {
  const start = parsePerDayYmdToLocalDate(ymd);
  if (Number.isNaN(+start)) return "";
  const cur = new Date(start);
  cur.setDate(cur.getDate() + deltaDays);
  const y = cur.getFullYear();
  const mo = String(cur.getMonth() + 1).padStart(2, "0");
  const da = String(cur.getDate()).padStart(2, "0");
  return `${y}-${mo}-${da}`;
}

/**
 * Latest valid inclusive end date for a per-day range when start is startYmd (7 calendar days inclusive).
 * @param {string} startYmd
 * @returns {string}
 */
export function getPerDayRangeMaxEndYmd(startYmd) {
  return addCalendarDaysYmd(startYmd, PER_DAY_RANGE_MAX_DAYS - 1);
}

/**
 * Parses YYYY-MM-DD at local midnight. Bad input yields Invalid Date.
 * @param {string} ymd
 * @returns {Date}
 */
export function parsePerDayYmdToLocalDate(ymd) {
  if (!ymd || typeof ymd !== "string") return new Date(NaN);
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(y, m - 1, d);
}

/**
 * Local calendar date to YYYY-MM-DD for stored `from` / `to` strings.
 * @param {Date|null|undefined} date
 * @returns {string}
 */
export function formatLocalDateToPerDayYmd(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const y = date.getFullYear();
  const mo = String(date.getMonth() + 1).padStart(2, "0");
  const da = String(date.getDate()).padStart(2, "0");
  return `${y}-${mo}-${da}`;
}

/** Normalize stored dates to YYYY-MM-DD for dayPlans lookup. */
export function normalizeChartDateYmd(value) {
  if (!value) return "";
  if (typeof value === "string") {
    const iso = value.match(/^(\d{4}-\d{2}-\d{2})/);
    if (iso) return iso[1];
    const dmy = value.trim().match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (dmy) return `${dmy[3]}-${dmy[2]}-${dmy[1]}`;
  }
  if (value && typeof value === "object" && value.seconds != null) {
    return formatLocalDateToPerDayYmd(new Date(value.seconds * 1000));
  }
  const d = value instanceof Date ? value : new Date(value);
  if (!Number.isNaN(d.getTime())) {
    return formatLocalDateToPerDayYmd(d);
  }
  return "";
}

/**
 * Value for react-datepicker `selected` when bound to per-day YYYY-MM-DD state.
 * @param {string} ymd
 * @returns {Date|null}
 */
export function perDayYmdToPickerSelected(ymd) {
  if (!ymd) return null;
  const parsed = parsePerDayYmdToLocalDate(ymd);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/** Local weekday short names; index matches Date#getDay(). */
const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/**
 * @param {string} ymd - YYYY-MM-DD
 * @returns {string} e.g. "2026-04-07 (Tue)"; invalid ymd returns input unchanged
 */
export function formatPerDayDateWithWeekday(ymd) {
  if (!ymd || typeof ymd !== "string") return ymd || "";
  const parsed = parsePerDayYmdToLocalDate(ymd);
  if (Number.isNaN(parsed.getTime())) return ymd;
  return `${ymd} (${WEEKDAY_LABELS[parsed.getDay()]})`;
}

/**
 * Inclusive list of YYYY-MM-DD strings from fromStr to toStr (local).
 */
export function enumerateDatesInclusive(fromStr, toStr) {
  const dates = [];
  const start = parsePerDayYmdToLocalDate(fromStr);
  const end = parsePerDayYmdToLocalDate(toStr);
  const cur = new Date(start);
  while (cur <= end) {
    const y = cur.getFullYear();
    const mo = String(cur.getMonth() + 1).padStart(2, "0");
    const da = String(cur.getDate()).padStart(2, "0");
    dates.push(`${y}-${mo}-${da}`);
    cur.setDate(cur.getDate() + 1);
  }
  return dates;
}

/**
 * Maps source `dayPlans` onto target date keys by **column index**: day 1 of the
 * source range fills day 1 of the target range, etc. Used when cloning meals into
 * the current draft without changing that draft’s `from`/`to`.
 *
 * @param {Record<string, Record<string, unknown>>} sourceDayPlans - Source chart `dayPlans` (keys `YYYY-MM-DD`).
 * @param {string} sourceFrom - Source chart inclusive start.
 * @param {string} sourceTo - Source chart inclusive end.
 * @param {string} targetFrom - Current draft inclusive start (headers stay on this range).
 * @param {string} targetTo - Current draft inclusive end.
 * @returns {Record<string, Record<string, unknown>>} Only target keys that received a slice; merge into existing `dayPlans` with `{ ...prev, ...out }`.
 */
export function remapDayPlansPositionally(
  sourceDayPlans,
  sourceFrom,
  sourceTo,
  targetFrom,
  targetTo
) {
  const sourceDates = enumerateDatesInclusive(sourceFrom, sourceTo);
  const targetDates = enumerateDatesInclusive(targetFrom, targetTo);
  const pairCount = Math.min(sourceDates.length, targetDates.length);
  const out = {};
  for (let i = 0; i < pairCount; i++) {
    const srcKey = sourceDates[i];
    const tgtKey = targetDates[i];
    const slice = sourceDayPlans[srcKey];
    out[tgtKey] =
      slice != null ? JSON.parse(JSON.stringify(slice)) : {};
  }
  return out;
}

export function isPerDayLayoutChart(doc) {
  return doc?.layoutVariant === LAYOUT_VARIANT_PER_DAY && doc?.dayPlans;
}

/** Meals for client table: top-level slot, else all days in dayPlans. */
export function getClientTableSlotMeals(dietChart, slotKey) {
  const top = dietChart?.[slotKey];
  if (Array.isArray(top) && top.length > 0) {
    return top;
  }
  if (!dietChart?.dayPlans || typeof dietChart.dayPlans !== "object") {
    return Array.isArray(top) ? top : [];
  }
  const meals = [];
  Object.values(dietChart.dayPlans).forEach((day) => {
    if (!day || typeof day !== "object") return;
    const list = day[slotKey];
    if (Array.isArray(list) && list.length > 0) {
      meals.push(...list);
    }
  });
  return meals;
}

/** PDF rows for one selected dietplan (client portal). */
export function buildClientPortalChartData(dietChart) {
  if (!dietChart?.dietChartId) return [];

  if (isPerDayLayoutChart(dietChart)) {
    const slices = expandPerDayChartToPdfSlices(dietChart);
    return slices?.length ? slices : [];
  }

  const table = [];
  MEAL_SLOT_KEYS.forEach((key) => {
    const list = dietChart[key];
    if (Array.isArray(list) && list.length > 0) {
      table.push({ timing: key, list });
    }
  });
  if (table.length === 0) return [];

  const fromYmd =
    normalizeChartDateYmd(dietChart.from) ||
    normalizeChartDateYmd(dietChart.date) ||
    formatLocalDateToPerDayYmd(new Date());

  return [
    {
      dietChartId: dietChart.dietChartId,
      date: dietChart.date || fromYmd,
      weekPlan: dietChart.weekPlan,
      chartType: dietChart.chartType,
      notes: dietChart.notes || "",
      from: fromYmd,
      to: fromYmd,
      layoutVariant: LAYOUT_VARIANT_PER_DAY,
      table,
    },
  ];
}

/**
 * Builds PDF chartData rows: one entry per calendar day that has at least one meal slot with items.
 * Client guidelines are not repeated per slice: first emitted slice carries perDayTrailingNotes for the server PDF.
 */
export function expandPerDayChartToPdfSlices(dietChart) {
  if (!isPerDayLayoutChart(dietChart)) return null;
  const from = normalizeChartDateYmd(dietChart.from);
  const to = normalizeChartDateYmd(dietChart.to);
  if (!from || !to) return null;
  const dates = enumerateDatesInclusive(from, to);
  const slices = [];
  const trailingGuidelines = String(dietChart.notes || "").trim();
  let isFirstEmittedSlice = true;
  for (const dateStr of dates) {
    const day = dietChart.dayPlans?.[dateStr] || {};
    const table = [];
    for (const timing of Object.keys(day)) {
      const list = day[timing];
      if (Array.isArray(list) && list.length > 0) {
        table.push({ timing, list });
      }
    }
    if (table.length === 0) {
      continue;
    }
    slices.push({
      dietChartId: dietChart.dietChartId,
      date: dateStr,
      weekPlan: dietChart.weekPlan,
      chartType: dietChart.chartType,
      notes: "",
      perDayTrailingNotes: isFirstEmittedSlice ? trailingGuidelines : "",
      from: dateStr,
      to: dateStr,
      layoutVariant: LAYOUT_VARIANT_PER_DAY,
      table,
    });
    isFirstEmittedSlice = false;
  }
  return slices;
}
